import { phraseBook, wordBook } from "../data/dictionary";
import { capitalize, normalize, stripPunctuation } from "./text";

export interface TranslationToken {
  source: string;
  target: string;
  known: boolean;
}

export interface TranslationResult {
  text: string;
  tokens: TranslationToken[];
  supported: boolean;
}

function invert(record: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(record).map(([k, v]) => [normalize(v), k]));
}

const reversePhrases = invert(phraseBook);
const reverseWords = invert(wordBook);

function lookupWord(word: string, dict: Record<string, string>): string | undefined {
  return dict[normalize(word)];
}

/** Demo translator: covers English ↔ Spanish with a small phrase book and word list. */
export function translate(input: string, from: string, to: string): TranslationResult {
  const trimmed = input.trim();
  if (!trimmed) return { text: "", tokens: [], supported: true };

  const forward = from === "en" && to === "es";
  const backward = from === "es" && to === "en";
  if (!forward && !backward) {
    return { text: trimmed, tokens: [], supported: false };
  }

  const phrases = forward ? phraseBook : reversePhrases;
  const words = forward ? wordBook : reverseWords;
  const tokenDict = forward ? reverseWords : wordBook;
  const key = normalize(trimmed);

  const phraseHit = phrases[key];
  if (phraseHit) {
    const text = capitalize(phraseHit);
    const tokens = text.split(" ").map((t) => {
      const back = lookupWord(stripPunctuation(t), tokenDict);
      return { source: back ?? t, target: t, known: Boolean(back) };
    });
    return { text, tokens, supported: true };
  }

  const tokens = trimmed.split(/\s+/).map((raw) => {
    const hit = lookupWord(stripPunctuation(raw), words);
    return { source: raw, target: hit ?? stripPunctuation(raw), known: Boolean(hit) };
  });
  return { text: capitalize(tokens.map((t) => t.target).join(" ")), tokens, supported: true };
}
